"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  Columns3,
  Search,
  BriefcaseBusiness,
  History,
} from "lucide-react";
import { fmtNumber } from "@/lib/format";
import type { AccountRow, PositionTableRow, OrderTableRow } from "./model";

type Column = {
  id: string;
  label: string;
  kind?: "number" | "price" | "pnl" | "time" | "percent" | "legs";
  hidden?: boolean;
  derived?: boolean;
};
const POSITION_COLUMNS: Column[] = [
  { id: "symbol", label: "Symbol" },
  { id: "instrument_type", label: "Instrument" },
  { id: "currency", label: "Currency" },
  { id: "quantity", label: "Quantity", kind: "number" },
  { id: "cost_price", label: "Avg. cost", kind: "price" },
  { id: "last_price", label: "Last price", kind: "price" },
  { id: "unrealized_profit_loss", label: "Unrealized P/L", kind: "pnl" },
  { id: "position_id", label: "Position ID", hidden: true },
  { id: "option_strategy", label: "Option strategy", hidden: true },
  { id: "legs", label: "Legs", kind: "legs", hidden: true },
];
const ORDER_COLUMNS: Column[] = [
  { id: "symbol", label: "Symbol" },
  { id: "side", label: "Side" },
  { id: "status", label: "Status" },
  { id: "order_type", label: "Order type" },
  { id: "total_quantity", label: "Order qty", kind: "number" },
  { id: "filled_quantity", label: "Filled qty", kind: "number" },
  { id: "filled_price", label: "Filled price", kind: "price" },
  { id: "place_time", label: "Placed", kind: "time" },
  { id: "filled_time", label: "Filled", kind: "time" },
  { id: "instrument_type", label: "Instrument", hidden: true },
  { id: "limit_price", label: "Limit price", kind: "price", hidden: true },
  { id: "stop_price", label: "Stop price", kind: "price", hidden: true },
  { id: "time_in_force", label: "TIF", hidden: true },
  { id: "support_trading_session", label: "Trading session", hidden: true },
  { id: "order_id", label: "Order ID", hidden: true },
  { id: "client_order_id", label: "Client order ID", hidden: true },
  { id: "option_strategy", label: "Option strategy", hidden: true },
  { id: "legs", label: "Legs", kind: "legs", hidden: true },
  {
    id: "realized_pl",
    label: "Realized P/L (FIFO)",
    kind: "pnl",
    hidden: true,
    derived: true,
  },
  {
    id: "realized_pl_pct",
    label: "Return % (FIFO)",
    kind: "percent",
    hidden: true,
    derived: true,
  },
];
const PAGE_SIZE = 25;
const numberFormat = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 6,
});
const dateFormat = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "Asia/Bangkok",
  hour12: false,
});
function formatPrice(value: number, row: AccountRow, positions: boolean) {
  if (
    positions &&
    typeof row.currency === "string" &&
    /^[A-Z]{3}$/.test(row.currency)
  ) {
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: row.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      }).format(value);
    } catch {}
  }
  return fmtNumber(value, 2);
}
function tone(value: number) {
  return value > 0 ? "wb-positive" : value < 0 ? "wb-negative" : "wb-neutral";
}
function parseLegs(value: string): unknown[] | null {
  try {
    const legs: unknown = JSON.parse(value);
    return Array.isArray(legs) ? legs : null;
  } catch {
    return null;
  }
}
function Cell({
  row,
  column,
  positions,
}: {
  row: AccountRow;
  column: Column;
  positions: boolean;
}) {
  const value = row[column.id];
  if (value == null || value === "")
    return <span className="wb-neutral">—</span>;
  if (column.id === "symbol")
    return (
      <span className="wa-symbol">
        <span className="wa-symbolIcon">{String(value).slice(0, 1)}</span>
        {value}
      </span>
    );
  if (column.id === "side")
    return (
      <span className={`wa-badge ${value === "BUY" ? "wa-lime" : "wa-purple"}`}>
        {value}
      </span>
    );
  if (column.id === "status")
    return (
      <span
        className={`wa-badge ${value === "FILLED" ? "wa-lime" : value === "PARTIAL_FILLED" ? "wa-purple" : /FAILED|REJECT/.test(String(value)) ? "wa-red" : "wa-muted"}`}
      >
        {String(value).replaceAll("_", " ")}
      </span>
    );
  if (column.id === "instrument_type")
    return <span className="wa-badge wa-purple">{value}</span>;
  if (column.kind === "time") {
    const date = new Date(String(value));
    return Number.isNaN(date.getTime()) ? (
      <span>—</span>
    ) : (
      <time dateTime={String(value)} title={String(value)}>
        {dateFormat.format(date)}
      </time>
    );
  }
  if (column.kind === "legs") {
    const legs = parseLegs(String(value));
    if (!legs) return <span>—</span>;
    return (
      <details className="wa-legs">
        <summary>
          {legs.length} {legs.length === 1 ? "leg" : "legs"}
        </summary>
        <pre>{JSON.stringify(legs, null, 2)}</pre>
      </details>
    );
  }

  if (typeof value === "number") {
    if (column.kind === "number")
      return <span>{numberFormat.format(value)}</span>;
    if (column.kind === "percent")
      return (
        <span className={tone(value)}>
          {value > 0 ? "+" : ""}
          {fmtNumber(value)}%
        </span>
      );
    return (
      <span className={column.kind === "pnl" ? tone(value) : undefined}>
        {formatPrice(value, row, positions)}
      </span>
    );
  }
  return <span title={String(value)}>{value}</span>;
}

function matchesFilter(row: AccountRow, value: string, positions: boolean) {
  return (
    value === "All" ||
    (positions
      ? row.instrument_type === value
      : value === "CANCELED"
        ? ["CANCELED", "CANCELLED"].includes(String(row.status))
        : row.status === value)
  );
}
export default function AccountTable(
  props:
    | { kind: "positions"; rows: PositionTableRow[] }
    | { kind: "orders"; rows: OrderTableRow[] },
) {
  const positions = props.kind === "positions";
  const columns = positions ? POSITION_COLUMNS : ORDER_COLUMNS;
  const rows: AccountRow[] = props.rows;
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({
    id: positions ? "symbol" : "place_time",
    asc: positions,
  });
  const [hidden, setHidden] = useState(
    () => new Set(columns.filter((c) => c.hidden).map((c) => c.id)),
  );
  const picker = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (picker.current && !picker.current.contains(e.target as Node))
        picker.current.open = false;
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape" && picker.current) picker.current.open = false;
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", key);
    };
  }, []);
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter(
      (row) =>
        matchesFilter(row, filter, positions) &&
        (!query ||
          [
            row.symbol,
            row.instrument_type,
            row.order_id,
            row.position_id,
            row.side,
            row.status,
          ].some(
            (value) =>
              value != null && String(value).toLowerCase().includes(query),
          )),
    );
  }, [rows, search, filter, positions]);
  const sorted = useMemo(
    () =>
      [...filtered].sort((a, b) => {
        const av = a[sort.id],
          bv = b[sort.id];
        if (av == null) return bv == null ? 0 : 1;
        if (bv == null) return -1;
        const result =
          typeof av === "number" && typeof bv === "number"
            ? av - bv
            : String(av).localeCompare(String(bv));
        return sort.asc ? result : -result;
      }),
    [filtered, sort],
  );
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const shown = sorted.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const visible = columns.filter((c) => !hidden.has(c.id));
  const choices = positions
    ? ["All", "EQUITY", "OPTION"]
    : ["All", "FILLED", "PARTIAL_FILLED", "CANCELED", "FAILED"];
  const Icon = positions ? BriefcaseBusiness : History;
  return (
    <div className="wb-card wa-tableCard">
      <div className="wa-tableTitle">
        <h2>
          {positions ? "Open positions" : "Order history"}
          <span className="wb-count">{rows.length}</span>
        </h2>
        <details ref={picker} className="wa-picker">
          <summary className="wb-accountButton">
            <Columns3 size={13} />
            คอลัมน์
          </summary>
          <div className="wa-pickerPanel">
            {columns
              .filter((c) => c.id !== "symbol")
              .map((c) => (
                <label
                  key={c.id}
                  title={
                    c.derived
                      ? "คำนวณจากประวัติที่บันทึกไว้ ก่อนค่าธรรมเนียม"
                      : `Webull: ${c.id}`
                  }
                >
                  <input
                    type="checkbox"
                    checked={!hidden.has(c.id)}
                    onChange={() =>
                      setHidden((previous) => {
                        const next = new Set(previous);
                        if (next.has(c.id)) next.delete(c.id);
                        else next.add(c.id);
                        return next;
                      })
                    }
                  />
                  {c.label}
                </label>
              ))}
          </div>
        </details>
      </div>
      <div className="wa-toolbar">
        <div className="wa-filters">
          {choices.map((value) => (
            <button
              type="button"
              key={value}
              aria-pressed={filter === value}
              onClick={() => {
                setFilter(value);
                setPage(1);
              }}
              className={filter === value ? "wa-filterActive" : ""}
            >
              {value === "All" ? "All" : value.replaceAll("_", " ")}
              <span>
                {
                  rows.filter((row) => matchesFilter(row, value, positions))
                    .length
                }
              </span>
            </button>
          ))}
        </div>
        <div className="wa-search">
          <Search size={14} />
          <input
            type="search"
            aria-label="ค้นหาในตาราง"
            placeholder="Search symbol…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </div>
      {rows.length ? (
        <div className="wa-tableScroll">
          <table>
            <thead>
              <tr>
                {visible.map((column) => (
                  <th
                    key={column.id}
                    title={
                      column.derived
                        ? "คำนวณด้วย FIFO จากประวัติที่บันทึกไว้ ก่อนค่าธรรมเนียม"
                        : `Webull: ${column.id}`
                    }
                    aria-sort={
                      sort.id === column.id
                        ? sort.asc
                          ? "ascending"
                          : "descending"
                        : undefined
                    }
                    className={
                      column.kind &&
                      column.kind !== "time" &&
                      column.kind !== "legs"
                        ? "wa-numeric"
                        : ""
                    }
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setSort((current) => ({
                          id: column.id,
                          asc: current.id === column.id ? !current.asc : false,
                        }))
                      }
                    >
                      {column.label}
                      {sort.id === column.id &&
                        (sort.asc ? (
                          <ArrowUp size={11} />
                        ) : (
                          <ArrowDown size={11} />
                        ))}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => (
                <tr key={row.id}>
                  {visible.map((column) => (
                    <td
                      key={column.id}
                      className={
                        column.kind &&
                        column.kind !== "time" &&
                        column.kind !== "legs"
                          ? "wa-numeric"
                          : ""
                      }
                    >
                      <Cell row={row} column={column} positions={positions} />
                    </td>
                  ))}
                </tr>
              ))}
              {!shown.length && (
                <tr>
                  <td colSpan={visible.length} className="wa-noMatch">
                    ไม่มีข้อมูลที่ตรงเงื่อนไข
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="wa-empty">
          <Icon size={32} strokeWidth={1} />
          <span>
            {positions ? "ยังไม่มี Position" : "ยังไม่มีประวัติคำสั่ง"}
          </span>
        </div>
      )}
      <footer className="wa-tableFooter">
        <span>
          {sorted.length ? (current - 1) * PAGE_SIZE + 1 : 0}–
          {Math.min(current * PAGE_SIZE, sorted.length)} / {sorted.length}
        </span>
        <div>
          <button
            type="button"
            aria-label="หน้าก่อน"
            disabled={current === 1}
            onClick={() => setPage(current - 1)}
          >
            <ChevronLeft size={14} />
          </button>
          <span>
            {current} / {totalPages}
          </span>
          <button
            type="button"
            aria-label="หน้าถัดไป"
            disabled={current === totalPages}
            onClick={() => setPage(current + 1)}
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </footer>
    </div>
  );
}
